import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatCents, formatDateTime } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { DeliveryChecklist, STEP_KIND, type OrderDelivery } from "./delivery";
import { useContactNames } from "./useContactNames";

/** Billing → Paid, but something is missing (crm-billing: billing_delivery_problems). Looking changes nothing. */
export function DeliveryProblemsPage() {
  const [brand, setBrand] = useState<number | "">("");
  const [kind, setKind] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const q = useQuery({
    queryKey: ["delivery-problems", brand, kind, since, until],
    queryFn: () =>
      api<{ checked: number; orders: OrderDelivery[] }>(
        "/api/v1/billing/delivery-problems" + qs({ brand_id: brand || undefined, kind: kind || undefined, since: since || undefined, until: until || undefined }),
      ),
  });
  const rows = q.data?.orders ?? [];
  const name = useContactNames(rows.map((o) => o.contact_id));
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Paid, but something is missing" sub="Paid orders whose email, course access, class registration or print job didn't finish">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Opening this page changes nothing and sends nothing. Fix a problem with the usual action on the order, contact or event. Check Lulu&apos;s dashboard before resending a
          print job, so a book isn&apos;t printed twice. Times are Mountain Time.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Select className="h-8 w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
            <option value="">All brands</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.short}
              </option>
            ))}
          </Select>
          <Select className="h-8 w-auto" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Problem">
            <option value="">Any problem</option>
            {Object.entries(STEP_KIND).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1 text-sm">
            Paid from
            <Input type="date" className="h-8 w-auto" value={since} onChange={(e) => setSince(e.target.value)} aria-label="Paid from" />
          </label>
          <label className="flex items-center gap-1 text-sm">
            to
            <Input type="date" className="h-8 w-auto" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="Paid to" />
          </label>
          {q.data && <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">{q.data.checked} paid orders checked (last 30 days unless you pick dates)</span>}
        </div>
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
        {!q.isLoading && !q.error && rows.length === 0 && <Empty title="Every paid order is delivered." />}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Order</TH>
                <TH>Person</TH>
                <TH>Paid (MT)</TH>
                <TH>What&apos;s missing</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((o) => (
                <TR key={o.order_id}>
                  <TD className="align-top">
                    <div className="flex items-center gap-2">
                      <BrandMark id={o.brand_id} />
                      <Link to="/billing/orders/$orderId" params={{ orderId: String(o.order_id) }} className="num font-medium text-[var(--color-primary)] hover:underline">
                        #{o.order_id}
                      </Link>
                    </div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">
                      {o.products.join(", ")}, {formatCents(o.total_cents)}
                    </div>
                  </TD>
                  <TD className="max-w-[12rem] truncate align-top">
                    <Link to="/contacts/$contactId" params={{ contactId: String(o.contact_id) }} className="hover:underline">
                      {name(o.contact_id)}
                    </Link>
                  </TD>
                  <TD className="num whitespace-nowrap align-top text-xs">{o.paid_at ? formatDateTime(o.paid_at) : ""}</TD>
                  <TD className="align-top">
                    <DeliveryChecklist steps={o.steps} onlyProblems />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
